import { useState, useEffect } from 'react';

interface PWAInstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWACapabilities {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  canShare: boolean;
  hasCamera: boolean;
  hasVibration: boolean;
  hasNotifications: boolean;
}

export const usePWA = () => {
  const [installPrompt, setInstallPrompt] = useState<PWAInstallPrompt | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [capabilities, setCapabilities] = useState<PWACapabilities>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    canShare: 'share' in navigator,
    hasCamera: 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
    hasVibration: 'vibrate' in navigator,
    hasNotifications: 'Notification' in window
  });

  useEffect(() => {
    // Check if app is installed
    const checkIfInstalled = () => {
      const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebApp = (window.navigator as any).standalone === true;
      const isInstalled = isInStandaloneMode || isInWebApp;
      
      setIsInstalled(isInstalled);
      setCapabilities(prev => ({ ...prev, isInstalled }));
    };

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as PWAInstallPrompt);
      setCapabilities(prev => ({ ...prev, isInstallable: true }));
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      console.log('ETrax PWA was installed');
      setInstallPrompt(null);
      setIsInstalled(true);
      setCapabilities(prev => ({ 
        ...prev, 
        isInstalled: true, 
        isInstallable: false 
      }));
    };

    // Listen for online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      setCapabilities(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setIsOnline(false);
      setCapabilities(prev => ({ ...prev, isOnline: false }));
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial checks
    checkIfInstalled();

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('ETrax Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.error('ETrax Service Worker registration failed:', error);
        });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const installApp = async () => {
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('ETrax PWA install accepted');
        setInstallPrompt(null);
        setCapabilities(prev => ({ ...prev, isInstallable: false }));
        return true;
      } else {
        console.log('ETrax PWA install dismissed');
        return false;
      }
    } catch (error) {
      console.error('ETrax PWA install error:', error);
      return false;
    }
  };

  const shareContent = async (data: ShareData) => {
    if (!capabilities.canShare) {
      // Fallback for browsers without Web Share API
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(data.url || window.location.href);
          return { success: true, method: 'clipboard' };
        } catch (error) {
          return { success: false, error: 'Failed to copy to clipboard' };
        }
      }
      return { success: false, error: 'Sharing not supported' };
    }

    try {
      await navigator.share(data);
      return { success: true, method: 'native' };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Share cancelled by user' };
      }
      return { success: false, error: 'Share failed' };
    }
  };

  const requestNotificationPermission = async () => {
    if (!capabilities.hasNotifications) {
      return { granted: false, error: 'Notifications not supported' };
    }

    try {
      const permission = await Notification.requestPermission();
      return { granted: permission === 'granted', permission };
    } catch (error) {
      return { granted: false, error: 'Failed to request permission' };
    }
  };

  const showNotification = (title: string, options?: NotificationOptions) => {
    if ('serviceWorker' in navigator && Notification.permission === 'granted') {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          icon: '/icons/icon-192x192.png',
          badge: '/icons/badge-72x72.png',
          ...options
        });
      });
    }
  };

  const vibrate = (pattern: number | number[]) => {
    if (capabilities.hasVibration) {
      navigator.vibrate(pattern);
    }
  };

  const addToHomeScreen = () => {
    // For iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    if (isIOS && isSafari && !isInstalled) {
      return {
        showInstructions: true,
        instructions: [
          'Tap the Share button at the bottom of the screen',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to install ETrax on your device'
        ]
      };
    }
    
    return { showInstructions: false, instructions: [] };
  };

  return {
    capabilities,
    isInstalled,
    isOnline,
    installApp,
    shareContent,
    requestNotificationPermission,
    showNotification,
    vibrate,
    addToHomeScreen
  };
};